import { TenantRecordDto } from '../../../platform/tenants/dtos/tenant-record.dto';
import { PageFormatEnum } from '../../../shared/modules/data-print';
import { PageOrientationEnum } from '../../../shared/modules/data-print/enums/page-orientation.enum';
import { UserRecordDto } from '../../../shared/modules/users/dtos/user-record.dto';
import type { TranslationCatalogEntry } from '../../../shared/types/translation-catalog-entry';
import { TenantSettingsDto } from '../../settings/dtos/tenant-settings.dto';
import { ReportVariableDto } from '../dtos/report-variable.dto';

/**
 * Report data passed to the renderer
 * @description
 * This object contains all the data needed to render a report
 * with most properties being optional or containing only a subset of the data
 * of the associated entity.
 *
 * This is an intentional design choice to allow for flexibility in the report
 * generation process and to avoid dragging in a lot of unnecessary data.
 */
export class ReportData {
	/**
	 * Report meta data
	 */
	meta!: {
		generatedAt?: Date;
		generatedBy?: {
			service: string;
			version: string;
		};
	};

	/**
	 * Report request details
	 */
	request!: {
		requestId?: string;
		timezone?: string;
	};

	/**
	 * System configuration data
	 */
	configuration!: {
		storageProviderUri?: string;
	};

	/**
	 * Settings data
	 */
	settings!: TenantSettingsDto;

	/**
	 * ISO 4217 code for money formatting in templates (from tenant settings).
	 */
	currencyIsoCode?: string;

	/**
	 * Tenant data
	 */
	tenant!: TenantRecordDto;

	/**
	 * User data
	 */
	user!: UserRecordDto;

	/**
	 * Report layout options
	 */
	layout!: {
		pageFormat: PageFormatEnum;
		pageOrientation: PageOrientationEnum;
		pageMargins: {
			top: string | number;
			right: string | number;
			bottom: string | number;
			left: string | number;
		};

		isESignable?: boolean;
		/** When false, confirmation email copy omits the PDF attachment. */
		hasPdfAttachment?: boolean;
	};

	/**
	 * Report styles
	 */
	styles!: {
		stylesUri: string;
		additionalStyles?: string[];
	};

	/**
	 * Report details
	 */
	report!: {
		id: number;
		name: string;
		title: string;
		subTitle?: string;
		description?: string;
		variables?: ReportVariableDto[];
	};

	/**
	 * Report template
	 */
	template!: {
		id: number;
		name: string;
	};

	/**
	 * Raw report data
	 */
	data!: {
		items?: { [key: string]: any };
		stats?: { [key: string]: any };
		totals?: { [key: string]: any };
	};

	/**
	 * Pre-resolved translation catalog for the active locale, with the `en`
	 * catalog merged in as the floor so missing keys still fall back to
	 * English without the renderer needing to know about other locales.
	 */
	i18n?: TranslationCatalogEntry;

	/**
	 * Facts describing the context of the report.
	 * @example
	 * ```ts
	 * const facts: Record<string, unknown> = {
	 *   customerName: 'John Doe',
	 *   vehicleName: 'Toyota Corolla',
	 * };
	 * ```
	 */
	facts?: Record<string, unknown>;
}
